import fetcher from '@/services/fetcher'
import type { Nullable, Winner } from '@/types'
import { createDateTimeComparator } from '@/utils/date-time-utils'
import { SiteSlug } from '@goodlifeplus/themes'
import { defineStore } from 'pinia'

type WinnersStoreState = {
  winners: Map<string, Winner>
  fetchPromise: Promise<Array<Winner>> | null
}

export const useWinnersStore = defineStore('winners', {
  state: (): WinnersStoreState => ({
    winners: new Map<string, Winner>(),
    fetchPromise: null,
  }),

  getters: {
    winnersArray(): Array<Winner> {
      return Array.from(this.winners.values()).sort(
        createDateTimeComparator(
          (w: Winner) => w.featured_at ?? w.drawn_at,
          'desc'
        )
      )
    },
    highlightedWinners(): Array<Winner> {
      return this.winnersArray.filter((w: Winner) => w.highlighted)
    },
  },

  actions: {
    async fetchWinners(): Promise<void | Array<Winner>> {
      // return the fetch winners promise to prevent multiple calls
      if (this.fetchPromise) {
        return this.fetchPromise
      }

      if (this.winners.size) {
        return this.winnersArray
      }

      try {
        const winnersFile =
          import.meta.env.GLP_SITE_SLUG === SiteSlug.TrueNorth
            ? '/data/true-north-winners.json'
            : '/data/winners.json'

        this.fetchPromise = fetcher
          .get<Winner>(winnersFile, false, {
            key: 'prize_code',
            map: this.winners,
          })
          .then(async () => {
            return this.winnersArray
          })
      } catch (error: any) {
        // TODO: Add Datadog custom log
      }

      return this.fetchPromise ?? this.winnersArray
    },
    async fetchWinnerByPrizeCode(prizeCode: string): Promise<Nullable<Winner>> {
      await this.fetchWinners()

      return (
        this.winnersArray.find((w: Winner) => w.prize_code === prizeCode) ??
        null
      )
    },
  },
})
